<template>

    <div v-if="contentLoading" class="flex items-center justify-center blur-background absolute inset-0 z-10" style="background: rgba(230,230,230,0.3)">
        <div class="animate-spin inline-block w-16 h-16 border-4 rounded-full" style="border-color: rgba(13, 59, 105, 0.4); border-right-color: transparent;"></div>
    </div>

    <div class="mb-2 -mx-1 flex flex-row justify-between items-center">
        <div class="mt-2">
            <button v-show="translationHistory.length > 1" @click="loadPrev" :class="translationHistoryIndex > 0 ? 'text-gray-600 opacity-70 hover:opacity-100' : 'text-gray-300 cursor-default'">
                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" style="fill: currentColor; transform: scaleX(-1);"><path d="M7.293 4.707 14.586 12l-7.293 7.293 1.414 1.414L17.414 12 8.707 3.293 7.293 4.707z"/></svg>
            </button>

            <button @click="loadNext" v-show="translationHistory.length > translationHistoryIndex + 1" class="text-gray-600 opacity-70 hover:opacity-100">
                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" style="fill: currentColor;"><path d="M7.293 4.707 14.586 12l-7.293 7.293 1.414 1.414L17.414 12 8.707 3.293 7.293 4.707z"/></svg>
            </button>
        </div>

        <button @click="translationsPaneVisible = false" class="flex flex-row text-gray-600 justify-center items-center pl-3 py-1 opacity-70 hover:opacity-100">
            <span style="font-size: 0.8rem; text-transform: uppercase;">Aizvērt</span>
            <span style="line-height: 0.3; font-size: 2rem; height: 1rem;">&times;</span>
        </button>
    </div>

    <div v-if="selectedWord">

        <div class="py-2 mb-3 w-full text-center hebrew-font text-slate-800" style="font-size: 1.8rem; line-height: 1; background: rgba(30, 41, 59, 0.08);">
            {{ selectedWordRaw }}
        </div>

        <div v-if="translations?.length">
            <h4 class="text-center text-slate-500 opacity-90 mb-2">Tulkojumi</h4>

            <div v-for="t in translations" class="mb-4 px-3 pt-2 pb-1" style="background: rgba(30, 41, 59, 0.05);">
                <div class="mb-2">
                    <span class="hebrew-font text-lg">
                        {{ t.hebrew }}
                    </span>
                </div>

                <p v-if="!t.derivatives?.length && !t.latvian?.length && !t.root && !t.definition && !t.notes">
                    <i class="text-gray-500 block my-4">Šim vārdam nav pievienots tulkojums.</i>
                </p>

                <p v-if="!t.derivatives?.length && !t.latvian?.length && !t.root && !t.notes">
                    <i class="text-gray-500 block my-4">Šī vārda sakne atspoguļo ideju par {{ t.definition }}.</i>
                </p>

                <div v-if="t.derivatives?.length" class="mt-4 mb-2">
                    <h5 class="font-bold text-sm">Atvasinājumi:</h5>

                    <p v-for="d in t.derivatives">
                        <span @click="translateHebrew(d.hebrew)" class="hebrew-font hebrew-word text-md">
                            {{ d.hebrew }}
                        </span>

                        <span v-if="d.latvian && d.latvian.length" class="text-gray-500">
                            - {{ d.latvian.map(translation => translation.text).join('; ') }}
                        </span>
                    </p>
                </div>

                <div v-if="t.latvian?.length" class="mt-4 mb-2">
                    <div v-if="t.latvian.length == 1">
                        {{ t.latvian[0].text }}
                    </div>

                    <ol v-else class="list-decimal pl-4">
                        <li v-for="trans in t.latvian" class="text-slate-500 pl-1">
                            <span class="text-black">{{ trans.text }}</span>
                        </li>
                    </ol>
                </div>

                <div v-if="t.origin > 0" class="mt-4 mb-2">
                    <h5 class="font-bold text-sm">Locījums no:</h5>

                    <p>
                        <span @click="translateHebrew(t.origin)" class="hebrew-font hebrew-word text-md">
                            {{ t.original_hebrew }}
                        </span>

                        <span v-if="t.quick_original_meaning" class="text-gray-500" v-html="' - ' + readableNotes(t.quick_original_meaning)"></span>
                    </p>
                </div>

                <div v-if="t.root" class="mt-4 mb-2">
                    <h5 class="font-bold text-sm">Vārda sakne:</h5>

                    <p>
                        <span @click="translateHebrew(t.root.id, true)" class="hebrew-font hebrew-word text-md">
                            {{ t.root.hebrew }}
                        </span>
                    </p>
                </div>

                <div v-if="t.origin_hebrew" class="mt-4 mb-2">
                    <h5 class="font-bold text-sm">Saknes izcelsme:</h5>

                    <p>
                        <span @click="translateHebrew(t.origin, true)" class="hebrew-font hebrew-word text-md">
                            {{ t.root.origin_hebrew }}
                        </span>
                    </p>
                </div>

                <div v-if="t.notes" class="mt-4 mb-2">
                    <h5 class="font-bold text-sm">Piezīmes:</h5>
                    <p v-html="readableNotes(t.notes)"></p>
                </div>
            </div>
        </div>

        <div v-else-if="contentLoaded">
            <p class="text-center text-slate-500 opacity-90 mb-2">Nekas par šo vārdu netika atrasts.</p>
        </div>
    </div>

</template>

<script setup>
    import { ref, computed, inject } from 'vue'

    const contentLoaded = ref(false)
    const contentLoading = ref(false)
    const selectedWord = ref('')
    const translations = ref([])

    const translationHistory = ref([])
    const translationHistoryIndex = ref(-1)

    const translationsPaneVisible = inject('translationsPaneVisible')

    const selectedWordRaw = computed(() => {
        const query = selectedWord.value

        if (!isNaN(query)) {
            return ''
        }

        return query.replace(/[^\u05D0-\u05EA]/g, '')
    })

    function translateHebrew(word, isRoot = false) {
        if (typeof word === 'string') {
            word = word.replace('׃', '')
        }

        contentLoaded.value = false
        contentLoading.value = true
        selectedWord.value = word
        translations.value = []

        translationsPaneVisible.value = true

        let loadingSpinnerDelay = setTimeout(() => {
            if (!contentLoaded.value && selectedWord.value == word) {
                contentLoading.value = true
            }
        }, 200)


        let method, isIdLookup = !isNaN(word)

        if (isRoot) {
            method = isIdLookup ? "rootid" : "roots"
        } else {
            method = isIdLookup ? "id" : "search"
        }

        axios.post('/api/dictionary/translate', {
            _token: window.csrfToken,
            query: word,
            method: method
        }).then(response => {

            if (response.data.hebrew != word) {
                // Paspēja jau nospiest uz citu vārdu, šo atbildi vairs nevajag.
                return
            }
            
            translations.value = response.data.results
            selectedWord.value = response.data.hebrew_raw

            translationHistory.value = translationHistory.value.slice(0, ++translationHistoryIndex.value)
            translationHistory.value[translationHistoryIndex.value] = response.data

        }).then(() => {
            clearTimeout(loadingSpinnerDelay)

            contentLoaded.value = true
            contentLoading.value = false
        })
    }


    function loadPrev() {
        if (translationHistoryIndex.value > 0) {
            let translationResponse = translationHistory.value[--translationHistoryIndex.value]

            translations.value = translationResponse?.results
            selectedWord.value = translationResponse?.hebrew_raw
        }
    }

    function loadNext() {
        if (translationHistoryIndex.value < translationHistory.value.length - 1) {
            let translationResponse = translationHistory.value[++translationHistoryIndex.value]

            translations.value = translationResponse?.results
            selectedWord.value = translationResponse?.hebrew_raw
        }
    }


    window.translateHebrew = translateHebrew


    function filterHebrew (heb) {
        return heb
            .replace(/a/g, "\u05D0")
            .replace(/b/gi, "\u05D1")
            .replace(/c/gi, "\u05E6")
            .replace(/d/gi, "\u05D3")
            .replace(/f|p/gi, "\u05E4")
            .replace(/g/gi, "\u05D2")
            .replace(/h/g, "\u05D7")
            .replace(/i|j|y/gi, "\u05D9")
            .replace(/k/gi, "\u05DB")
            .replace(/l/gi, "\u05DC")
            .replace(/m/gi, "\u05DE")
            .replace(/n/gi, "\u05E0")
            .replace(/q/gi, "\u05E7")
            .replace(/r/gi, "\u05E8")
            .replace(/s/gi, "\u05E1")
            .replace(/š/gi, "\u05E9")
            .replace(/t/g, "\u05EA")
            .replace(/u|v|w/gi, "\u05D5")
            .replace(/z/gi, "\u05D6")

            .replace(/A/g, "\u05E2") // ayin
            .replace(/H/g, "\u05D4") // he
            .replace(/T/g, "\u05D8") // tet
            
            .replace(/\u05C7/g, "\u05B8")// kamec katan => kamec
            .replace(/\u05F0/g, "\u05D5\u05D5")// yiddish double vav => vav+vav
            .replace(/\u05F2/g, "\u05D9\u05D9")// yiddish double jod => jod+jod
            .replace(/\u05F1/g, "\u05D5\u05D9")// yiddish vav jod => vav+jod
            .replace(/[^\u05D0-\u05EA\u05B0-\u05BC\u05C1\u05C2]/g,"")
    }

    function readableNotes (str) {
        str = str.replace(/Elohim/g, "Eloḫim")
        str = str.replace(/#\\(.*?)\/#/g, "<span class=\"hebrew-font hebrew-word text-md\" onclick=\"translateHebrew(this.innerHTML,1)\">$1</span>")
        str = str.replace(/\\\\(.*?)\/\//g, "<span class=\"hebrew-font hebrew-word text-md\" onclick=\"translateHebrew(this.innerHTML)\">$1</span>")
        str = str.replace(/::(.*?)::/g, "<span class=\"ancient-hebrew-font text-md\">$1</span>")
        str = str.replace(/\(\((.*?)\)\)/g, "<span class=\"text-gray-500\">($1)</span>")
        str = str.replace(/\n/g, "<br>")
        return str
    }


    function wordClassName (eng) {    
        const names = {
            "noun": "Lietvārds",
            "verb": "Darbības vārds",
            "adjective": "Īpašības vārds",
            "pronoun": "Vietniekvārds",
            "adverb": "Apstākļa vārds",
            "numeral": "Skaitļa vārds",
            "particle": "Partikula",
            "interjection": "Izsauksmes vārds",
            "preposition": "Prievārds",
            "conjunction": "Saistvārds",
            "proper": "Īpašvārds"
        }

        if (eng in names) {
            return names[eng]
        } else {
            return ""
        }
    }

    function wordGenderName (letter) {
        if (letter == "m") {
            return "Vīriešu dzimte"
        }
        if (letter == "f") {
            return "Sieviešu dzimte"
        }
        return ""
    }

    //                         if (res[i].conjugation) {
    //                             let conjugationProps = {}
    //                             let conjugationDetails = res[i].conjugation.split(":")
    //                             let gender

    //                             for (let ci = 0; ci < conjugationDetails.length; ci++) {
    //                                 const parts = conjugationDetails[ci].split("-")
    //                                 conjugationProps[parts[1]] = parts[0]
    //                             }

    //                             conjugationDetails = []

    //                             const wordClass = wordClassName(res[i].class)

    //                             if (wordClass) {
    //                                 conjugationDetails.push(wordClass)

    //                                 if (["noun", "pronoun", "proper"].indexOf(res[i].class) > -1) {
    //                                     if (gender = wordGenderName(res[i].gender)) {
    //                                         conjugationDetails.push(gender)
    //                                     }
    //                                 }

    //                                 if (res[i].class == "verb") {
    //                                     if ("possessive" in conjugationProps && conjugationProps.possessive.length == 3) {
    //                                         // if (gender = wordGenderName(conjugationProps.possessive[2])) {
    //                                         //     conjugationDetails.push(gender)
    //                                         // }
    //                                     } else {
    //                                         if (gender = wordGenderName(res[i].gender)) {
    //                                             conjugationDetails.push(gender)
    //                                         }
    //                                     }
    //                                 }
    //                             }

    //                             if ("possessive" in conjugationProps) {
    //                                 conjugationDetails.push("Piederības locījums")
    //                             }

    //                             if (conjugationDetails.length > 1) {
    //                                 htm += "<p class=\"gray\" style=\"margin:10px 0 0\"><small>" + conjugationDetails.join(" &middot; ") + "</small></p>"
    //                             }
    //                         }
    //                     }
                        
</script>
